/**
 * Per-vehicle drill-in from the Image Index. Loads metadata, renders the
 * photo gallery, and exposes a manual "Repush" button gated on the
 * repush_photos permission.
 *
 * Routes:
 *   /photos/vehicles/:vin?dealer=<slug>   — VIN'd road vehicles
 *   /photos/items/:stock?dealer=<slug>    — stock-only inventory (CTA
 *                                            campers/trailers with no VIN)
 * The dealer query param disambiguates the identifier across rooftops.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { ArrowLeft, Check, Download, Pencil, RefreshCw, Sparkles, Trash2, Upload, X } from "lucide-react"
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

import Layout from "../../components/Layout"
import ExportStatusChip from "../../components/photos/ExportStatusChip"
import InFeedStatusChip from "../../components/photos/InFeedStatusChip"
import ReprocessConfirmModal from "../../components/photos/ReprocessConfirmModal"
import { useAuth } from "../../context/AuthContext"
import { THEME } from "../../config/theme"
import { tint } from "../../utils/color"
import {
  RooftopOption,
  IndexPhoto,
  ProcessingJobSnapshot,
  UpdateSessionFields,
  VehicleDetail,
  deleteAllItemPhotos,
  deleteAllVehiclePhotos,
  downloadItemPhotosZip,
  downloadVehiclePhotosZip,
  getItemDetail,
  getItemPhotos,
  getItemProcessingJob,
  getVehicleDetail,
  getVehiclePhotos,
  getVehicleProcessingJob,
  listRooftops,
  approveItemPhotos,
  approveVehiclePhotos,
  processItemPhotos,
  processVehiclePhotos,
  reorderItemPhotos,
  reorderVehiclePhotos,
  repushVehicle,
  updateSession,
} from "../../api/imageIndex"
import {
  PresignPhotoSpec,
  completeSession,
  confirmPhoto,
  createSession,
  presignPhotos,
  uploadToR2,
} from "../../api/photoCapture"

const REPUSH_PERMISSION = "repush_photos"
// Same permission gates editing identity fields — both are operator
// actions on a session, not photographer self-service.
const EDIT_PERMISSION = "repush_photos"
const UPLOAD_PERMISSION = "manual_upload_photos"
const DELETE_PERMISSION = "delete_photos"
const PROCESS_PERMISSION = "process_photos"

// Poll interval while the worker is running. Long enough to avoid hammering
// the backend (a job typically takes 30-90s for a full session); short
// enough that the gallery refreshes within a useful operator-perceived
// window.
const PROCESSING_POLL_INTERVAL_MS = 3000

// Hard cap on how long the frontend will keep polling before giving up.
// 10 minutes covers a real worst-case session (≈30 photos × ~10s each with
// a cold-start U2Net download on top). Past this the worker is almost
// certainly dead — the next worker boot will reconcile the job to `failed`
// (see services/image_processing/recovery.py); until then the operator
// should be free of a spinning button.
const PROCESSING_POLL_TIMEOUT_MS = 10 * 60 * 1000

// Top-level job states that mean "worker is still working." When the job
// snapshot reports `done` or `failed`, polling stops.
const JOB_IN_FLIGHT_STATUSES = new Set<string>(["pending", "running"])

function displayPhotoUrl(photo: IndexPhoto): string {
  // Draft first (the operator is judging the newest run), then the
  // committed processed image, then the original.
  return photo.pending_processed_url ?? photo.processed_url ?? photo.original_url
}

const MOBILE_BREAKPOINT = 768

interface EditFormState {
  rooftop_slug: string
  vin: string
  stock: string
  year: string  // string so we can render the input cleanly; parsed at save
  make: string
  model: string
}

function emptyEditForm(): EditFormState {
  return { rooftop_slug: "", vin: "", stock: "", year: "", make: "", model: "" }
}

type Identifier =
  | { kind: "vin"; value: string }
  | { kind: "stock"; value: string }

export default function VehicleDetailPage() {
  // Both routes target this component. /photos/vehicles/:vin populates
  // params.vin; /photos/items/:stock populates params.stock. Exactly
  // one is present per render.
  const params = useParams<{ vin?: string; stock?: string }>()
  const [searchParams] = useSearchParams()
  const dealer = searchParams.get("dealer") ?? ""
  const navigate = useNavigate()
  const { hasPermission } = useAuth()

  const isMobile =
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT

  const identifier = useMemo<Identifier | null>(() => {
    if (params.vin) return { kind: "vin", value: params.vin }
    if (params.stock) return { kind: "stock", value: params.stock }
    return null
  }, [params.vin, params.stock])

  const [detail, setDetail] = useState<VehicleDetail | null>(null)
  const [rooftops, setRooftops] = useState<RooftopOption[]>([])
  const [photos, setPhotos] = useState<IndexPhoto[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [repushing, setRepushing] = useState<boolean>(false)
  const [repushError, setRepushError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState<boolean>(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [uploading, setUploading] = useState<boolean>(false)
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [deleting, setDeleting] = useState<boolean>(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [editing, setEditing] = useState<boolean>(false)
  const [editForm, setEditForm] = useState<EditFormState>(emptyEditForm())
  const [saving, setSaving] = useState<boolean>(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [reorderError, setReorderError] = useState<string | null>(null)
  // Photo ID of the most-recently reordered tile. Drives a transient
  // check badge that animates in-and-out on the tile itself so the user
  // sees the save confirmation in context, not as a corner toast.
  // The timer ref lets a rapid second reorder reset the badge timing.
  const [recentlyMovedPhotoId, setRecentlyMovedPhotoId] = useState<number | null>(null)
  const recentlyMovedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [processing, setProcessing] = useState<boolean>(false)
  const [processError, setProcessError] = useState<string | null>(null)
  // Open state for the force-reprocess confirmation modal — shown when the
  // operator clicks Process Images on a vehicle whose photos are all
  // already processed (a normal run would skip every one of them).
  const [confirmingReprocess, setConfirmingReprocess] = useState<boolean>(false)
  const [approving, setApproving] = useState<boolean>(false)
  const [approveError, setApproveError] = useState<string | null>(null)
  // Interval ref so cleanup on unmount + completion is straightforward.
  const processingPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const flagRecentlyMoved = useCallback((photoId: number) => {
    setRecentlyMovedPhotoId(photoId)
    if (recentlyMovedTimerRef.current) clearTimeout(recentlyMovedTimerRef.current)
    recentlyMovedTimerRef.current = setTimeout(() => setRecentlyMovedPhotoId(null), 1800)
  }, [])

  useEffect(() => {
    return () => {
      if (recentlyMovedTimerRef.current) clearTimeout(recentlyMovedTimerRef.current)
    }
  }, [])

  // Clean up the processing-poll interval on unmount so we never set state
  // on a stale component after the user navigates away mid-job.
  useEffect(() => {
    return () => {
      if (processingPollRef.current) clearInterval(processingPollRef.current)
    }
  }, [])

  // Dispatch fetchers by identifier kind so the rest of the component
  // doesn't have to branch on which route loaded it.
  const fetchDetail = useCallback(
    (d: string): Promise<VehicleDetail> => {
      if (!identifier) return Promise.reject(new Error("No identifier"))
      return identifier.kind === "vin"
        ? getVehicleDetail(identifier.value, d)
        : getItemDetail(identifier.value, d)
    },
    [identifier],
  )
  const fetchPhotos = useCallback(
    (d: string): Promise<IndexPhoto[]> => {
      if (!identifier) return Promise.reject(new Error("No identifier"))
      return identifier.kind === "vin"
        ? getVehiclePhotos(identifier.value, d)
        : getItemPhotos(identifier.value, d)
    },
    [identifier],
  )

  const refresh = useCallback(async () => {
    if (!identifier || !dealer) return
    setLoading(true)
    setError(null)
    try {
      const [d, ps] = await Promise.all([fetchDetail(dealer), fetchPhotos(dealer)])
      setDetail(d)
      setPhotos(ps)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load vehicle")
    } finally {
      setLoading(false)
    }
  }, [identifier, dealer, fetchDetail, fetchPhotos])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        if (!identifier || !dealer) {
          if (!cancelled) {
            setError("Missing identifier or dealer in URL.")
            setLoading(false)
          }
          return
        }
        const [d, ps] = await Promise.all([fetchDetail(dealer), fetchPhotos(dealer)])
        if (!cancelled) {
          setDetail(d)
          setPhotos(ps)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load vehicle")
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [identifier, dealer, fetchDetail, fetchPhotos])

  // Dealers list for the edit-mode dropdown. Loaded once.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await listRooftops()
        if (!cancelled) setRooftops(data)
      } catch {
        // Edit dropdown is non-critical — silent fallback to current dealer
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Lightbox keyboard navigation: Esc closes, Arrow keys cycle.
  useEffect(() => {
    if (lightboxIndex === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxIndex(null)
      else if (e.key === "ArrowRight") {
        setLightboxIndex((i) => (i === null ? null : Math.min(i + 1, photos.length - 1)))
      } else if (e.key === "ArrowLeft") {
        setLightboxIndex((i) => (i === null ? null : Math.max(i - 1, 0)))
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [lightboxIndex, photos.length])

  const handleStartEdit = useCallback(() => {
    if (!detail) return
    setEditForm({
      rooftop_slug: detail.rooftop_slug,
      vin: detail.vin ?? "",
      stock: detail.stock ?? "",
      year: detail.year !== null ? String(detail.year) : "",
      make: detail.make ?? "",
      model: detail.model ?? "",
    })
    setSaveError(null)
    setEditing(true)
  }, [detail])

  const handleCancelEdit = useCallback(() => {
    setEditing(false)
    setSaveError(null)
  }, [])

  const handleSaveEdit = useCallback(async () => {
    if (!detail || !detail.session_id) return
    const trimmedVin = editForm.vin.trim().toUpperCase()
    const trimmedStock = editForm.stock.trim()
    if (!trimmedVin && !trimmedStock) {
      setSaveError("Either VIN or Stock # is required.")
      return
    }
    if (!editForm.rooftop_slug) {
      setSaveError("Dealer is required.")
      return
    }
    const yearParsed = editForm.year.trim()
      ? Number.parseInt(editForm.year.trim(), 10)
      : null
    if (editForm.year.trim() && Number.isNaN(yearParsed as number)) {
      setSaveError("Year must be a number.")
      return
    }
    // Footgun guard: clearing a previously-set VIN is a reclassification
    // (this isn't a road vehicle anymore, it's a stock-only item), not a
    // typo. URL flips from /vehicles/:vin to /items/:stock, Repush button
    // disappears, in-feed status changes. Confirm before doing it.
    if (detail.vin && !trimmedVin) {
      const confirmed = window.confirm(
        "This will reclassify the item as stock-only (no VIN). The page URL " +
        "and some actions (like Repush) will change. Continue?",
      )
      if (!confirmed) return
    }
    setSaving(true)
    setSaveError(null)
    const fields: UpdateSessionFields = {
      rooftop_slug: editForm.rooftop_slug,
      vin: trimmedVin || null,
      stock: trimmedStock || null,
      year: yearParsed,
      make: editForm.make.trim() || null,
      model: editForm.model.trim() || null,
    }
    try {
      await updateSession(detail.session_id, fields)
      setEditing(false)
      // Navigate to the new URL if VIN/stock/dealer changed so refresh works.
      const vinChanged = trimmedVin !== (detail.vin ?? "")
      const stockChanged = trimmedStock !== (detail.stock ?? "")
      const dealerChanged = editForm.rooftop_slug !== detail.rooftop_slug
      if (vinChanged || stockChanged || dealerChanged) {
        // Pick the route by what's now available — VIN'd vehicles use
        // /vehicles, stock-only items use /items.
        const path = trimmedVin
          ? `/photos/vehicles/${encodeURIComponent(trimmedVin)}?dealer=${encodeURIComponent(editForm.rooftop_slug)}`
          : `/photos/items/${encodeURIComponent(trimmedStock)}?dealer=${encodeURIComponent(editForm.rooftop_slug)}`
        navigate(path, { replace: true })
      } else {
        await refresh()
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed")
    } finally {
      setSaving(false)
    }
  }, [detail, editForm, navigate, refresh])

  const handleRepush = useCallback(async () => {
    // Repush only makes sense for VIN'd vehicles right now — the export
    // pipeline is keyed by VIN. Stock-only items hide this button.
    if (identifier?.kind !== "vin" || !dealer) return
    setRepushing(true)
    setRepushError(null)
    try {
      await repushVehicle(identifier.value, dealer)
      await refresh()
    } catch (err) {
      setRepushError(err instanceof Error ? err.message : "Repush failed")
    } finally {
      setRepushing(false)
    }
  }, [identifier, dealer, refresh])

  const handleUploadClick = useCallback(() => {
    if (uploading) return
    fileInputRef.current?.click()
  }, [uploading])

  const fetchJob = useCallback(
    (jobId: number, d: string): Promise<ProcessingJobSnapshot> => {
      if (!identifier) return Promise.reject(new Error("No identifier"))
      return identifier.kind === "vin"
        ? getVehicleProcessingJob(identifier.value, jobId, d)
        : getItemProcessingJob(identifier.value, jobId, d)
    },
    [identifier],
  )

  // Kick off a processing job and poll it to completion. `force` is the
  // confirmed full re-run: the backend resets already-processed photos
  // first so the worker re-runs them with the current pipeline.
  const startProcessing = useCallback(async (force: boolean) => {
    if (!identifier || !dealer || processing) return
    setProcessing(true)
    setProcessError(null)
    let jobId: number
    try {
      const result =
        identifier.kind === "vin"
          ? await processVehiclePhotos(identifier.value, dealer, "all", force)
          : await processItemPhotos(identifier.value, dealer, "all", force)
      jobId = result.job_id
    } catch (err) {
      setProcessError(err instanceof Error ? err.message : "Could not start processing")
      setProcessing(false)
      return
    }

    // Poll the job snapshot — not the photos endpoint. The photo-state
    // approach had a race: on the first poll (3s after enqueue) every
    // photo is still null because the worker is cold-starting (rembg
    // U2Net download is 30-60s on first boot). The polling loop saw
    // all-nulls, declared "done", and stopped before the worker had
    // touched anything. The job has a durable status field that flips
    // pending → running before the first photo state change, and
    // running → done|failed after the last one.
    if (processingPollRef.current) clearInterval(processingPollRef.current)
    const pollStartedAt = Date.now()
    processingPollRef.current = setInterval(async () => {
      try {
        const [snapshot, latest] = await Promise.all([
          fetchJob(jobId, dealer),
          fetchPhotos(dealer),
        ])
        setPhotos(latest)
        const elapsed = Date.now() - pollStartedAt
        if (!JOB_IN_FLIGHT_STATUSES.has(snapshot.status)) {
          if (processingPollRef.current) clearInterval(processingPollRef.current)
          processingPollRef.current = null
          setProcessing(false)
          if (snapshot.status === "failed") {
            setProcessError(
              snapshot.error_text ?? "Image processing failed",
            )
          }
        } else if (elapsed >= PROCESSING_POLL_TIMEOUT_MS) {
          // Worker is almost certainly dead — release the operator from a
          // spinning button. The job stays `running` in the DB until the
          // next worker boot reconciles it; until then we just stop
          // tracking it client-side and surface the timeout.
          if (processingPollRef.current) clearInterval(processingPollRef.current)
          processingPollRef.current = null
          setProcessing(false)
          setProcessError(
            "Image processing is taking longer than expected. The worker may be " +
            "unreachable — try again, or refresh the page if photos eventually appear.",
          )
        }
      } catch {
        // Transient fetch error — keep polling. If the failure persists,
        // the user will see no progress and can refresh manually.
      }
    }, PROCESSING_POLL_INTERVAL_MS)
  }, [identifier, dealer, processing, fetchPhotos, fetchJob])

  const handleProcessClick = useCallback(() => {
    if (!identifier || !dealer || processing) return
    // When every photo is already processed, a normal run would skip all of
    // them (the worker's crash-retry guard) and finish having changed
    // nothing. Instead of silently doing nothing, confirm the full re-run —
    // this is how photos processed by an older pipeline version get
    // upgraded. The modal's confirm calls startProcessing(true).
    const allAlreadyProcessed =
      photos.length > 0 && photos.every((p) => p.processing_status === "processed")
    if (allAlreadyProcessed) {
      setConfirmingReprocess(true)
      return
    }
    void startProcessing(false)
  }, [identifier, dealer, processing, photos, startProcessing])

  // QC approval: promote the draft processed images to committed so the
  // export path ships them (backend copies pending_processed_url →
  // processed_url). Refused server-side with a 409 while a job is running.
  const handleApproveClick = useCallback(async () => {
    if (!identifier || !dealer || approving || processing) return
    setApproving(true)
    setApproveError(null)
    try {
      if (identifier.kind === "vin") {
        await approveVehiclePhotos(identifier.value, dealer)
      } else {
        await approveItemPhotos(identifier.value, dealer)
      }
      setPhotos(await fetchPhotos(dealer))
    } catch (err) {
      setApproveError(err instanceof Error ? err.message : "Could not approve photos")
    } finally {
      setApproving(false)
    }
  }, [identifier, dealer, approving, processing, fetchPhotos])

  const handleFilesSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])
      // Reset so picking the same files again re-fires onChange. Without
      // this, a retry-after-error has to start with a different selection.
      e.target.value = ""
      if (files.length === 0 || !identifier || !dealer || !detail) return

      setUploading(true)
      setUploadError(null)
      setUploadProgress({ done: 0, total: files.length })

      try {
        // 1. Create a fresh session attributed to the operator. Each upload
        //    is its own session so the photographer-effort metric stays
        //    consistent across capture, reshoot, and manual upload.
        //    For stock-only items we pass vin=null + the stock identifier;
        //    for VIN'd vehicles we pass the VIN + the existing stock.
        const session = await createSession({
          rooftop_slug: dealer,
          vin: identifier.kind === "vin" ? identifier.value : null,
          stock:
            identifier.kind === "stock" ? identifier.value : detail.stock,
          client_session_uuid: crypto.randomUUID(),
        })

        // 2. Read pixel dimensions in parallel so we can populate width/height
        //    on confirm. The image() trick is the simplest way without a
        //    server-side decode.
        const dims = await Promise.all(files.map(readImageDimensions))

        // 3. Mint presigned PUT URLs in one batch.
        const specs: PresignPhotoSpec[] = files.map((f, i) => ({
          client_photo_uuid: crypto.randomUUID(),
          display_order: i,
          content_type: f.type || "image/jpeg",
        }))
        const presigned = await presignPhotos(session.id, specs)

        // 4. PUT bytes to R2 then confirm. Browser caps concurrent
        //    connections per origin at ~6, which is also a reasonable
        //    parallelism for confirm calls.
        let done = 0
        await Promise.all(
          presigned.photos.map(async (p, i) => {
            const file = files[i]
            const dim = dims[i]
            const contentType = file.type || "image/jpeg"
            await uploadToR2(p.presigned_put_url, file, contentType)
            await confirmPhoto(session.id, p.photo_id, {
              width: dim?.width ?? null,
              height: dim?.height ?? null,
              bytes: file.size,
              captured_at: new Date(file.lastModified).toISOString(),
            })
            done += 1
            setUploadProgress({ done, total: files.length })
          }),
        )

        // 5. Mark ready. Completion auto-starts AI processing server-side;
        //    the exporter cron picks the session up on its next tick, and
        //    the operator can also hit Repush to push synchronously.
        await completeSession(session.id)

        // 6. Refresh metadata + gallery so the new session's photos appear.
        await refresh()

        // 7. Attach to the auto-started processing job so the operator sees
        //    live progress. The process endpoint dedupes onto the in-flight
        //    job rather than starting a second one. Skipped without the
        //    process permission — the job still runs server-side; the
        //    gallery just updates on a later visit.
        if (hasPermission(PROCESS_PERMISSION)) {
          void startProcessing(false)
        }
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : "Upload failed")
      } finally {
        setUploading(false)
        setUploadProgress(null)
      }
    },
    [identifier, dealer, detail, refresh, hasPermission, startProcessing],
  )

  const handleDownloadClick = useCallback(async () => {
    if (!identifier || !dealer || !detail) return
    setDownloading(true)
    setDownloadError(null)
    try {
      const blob =
        identifier.kind === "vin"
          ? await downloadVehiclePhotosZip(identifier.value, dealer)
          : await downloadItemPhotosZip(identifier.value, dealer)
      // Filename mirrors the server's Content-Disposition; we still need
      // a value here because some browsers ignore the header on blob: URLs.
      const stockPart = (detail.stock ?? "").trim()
      let filename: string
      if (identifier.kind === "vin") {
        filename = stockPart ? `${stockPart}_${identifier.value}.zip` : `${identifier.value}.zip`
      } else {
        filename = `${identifier.value}.zip`
      }
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Download failed")
    } finally {
      setDownloading(false)
    }
  }, [identifier, dealer, detail])

  const handleReorder = useCallback(
    async (newPhotoIds: number[], movedPhotoId: number) => {
      if (!identifier || !dealer) return
      // Optimistic update — snap the gallery to the new order before the
      // network round-trip so the drop feels instant. We persist the
      // server's authoritative response on success; on failure we refresh
      // to undo and surface the error.
      const photosById = new Map(photos.map((p) => [p.id, p]))
      const optimistic: IndexPhoto[] = newPhotoIds
        .map((id, i) => {
          const existing = photosById.get(id)
          return existing ? { ...existing, display_order: i } : null
        })
        .filter((p): p is IndexPhoto => p !== null)
      setPhotos(optimistic)
      setReorderError(null)
      try {
        const result =
          identifier.kind === "vin"
            ? await reorderVehiclePhotos(identifier.value, dealer, newPhotoIds)
            : await reorderItemPhotos(identifier.value, dealer, newPhotoIds)
        setPhotos(result.photos)
        flagRecentlyMoved(movedPhotoId)
      } catch (err) {
        setReorderError(err instanceof Error ? err.message : "Reorder failed")
        await refresh()
      }
    },
    [identifier, dealer, photos, refresh, flagRecentlyMoved],
  )

  const handleDeleteAllClick = useCallback(async () => {
    if (!identifier || !dealer || photos.length === 0) return
    // Browser confirm is enough for now — same affordance as Repush.
    // We can swap in a styled modal if the UX team pushes back.
    // No photo count: Delete All clears every shoot of the car, not only the
    // one on screen, so a count of what's shown could be too low. Ian
    // (2026-09-24): "I just want it to say delete all".
    const message =
      `Delete all photos for this vehicle? This cannot be undone.\n\n` +
      `Photos already pushed to the dealer site stay there until the next push.`
    if (!window.confirm(message)) return

    setDeleting(true)
    setDeleteError(null)
    try {
      if (identifier.kind === "vin") {
        await deleteAllVehiclePhotos(identifier.value, dealer)
      } else {
        await deleteAllItemPhotos(identifier.value, dealer)
      }
      // No refresh: with every shoot wiped the car has none left to load,
      // and the "not found" answer used to leave the deleted photos on
      // screen under an error. The car's details stay, so Upload Images
      // still works (it starts a new shoot); the wiped shoot is forgotten,
      // so Edit and Repush can't act on it.
      setPhotos([])
      setDetail((current) =>
        current ? { ...current, photo_count: 0, session_id: null } : current,
      )
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Delete failed")
    } finally {
      setDeleting(false)
    }
  }, [identifier, dealer, photos.length])

  // Repush hits the WordPress (or other) export pipeline by VIN.
  // Stock-only items don't have an export target wired yet, so the
  // button is hidden for them — the permission check still applies.
  const canRepush =
    hasPermission(REPUSH_PERMISSION) && identifier?.kind === "vin"
  const canEdit = hasPermission(EDIT_PERMISSION)
  const canUpload = hasPermission(UPLOAD_PERMISSION)
  const canDelete = hasPermission(DELETE_PERMISSION)
  const canProcess = hasPermission(PROCESS_PERMISSION)
  // Counts surfaced beside the Process button while a job is in flight so
  // the operator sees forward progress rather than a stalled spinner.
  const processingDoneCount = photos.filter(
    (p) => p.processing_status === "processed" || p.processing_status === "failed",
  ).length
  const lightboxPhoto = lightboxIndex !== null ? photos[lightboxIndex] ?? null : null

  return (
    <Layout>
      <div
        style={{
          ...containerStyle,
          ...(isMobile && { padding: THEME.spacing.md }),
        }}
      >
        <Link to="/photos/image-index" style={backLinkStyle}>
          <ArrowLeft size={14} strokeWidth={2} />
          <span>Image Index</span>
        </Link>

        <h1 style={titleStyle}>Vehicle Detail</h1>

        {error && <div style={errorBannerStyle}>{error}</div>}

        {detail && (
          <>
            {!editing && canRepush && detail.session_id !== null && (
              <div style={editHeaderRowStyle}>
                {repushError && (
                  <span style={repushErrorStyle}>{repushError}</span>
                )}
                <button
                  type="button"
                  onClick={handleRepush}
                  disabled={repushing}
                  style={repushButtonStyle(repushing)}
                >
                  <RefreshCw
                    size={14}
                    strokeWidth={2}
                    style={{
                      animation: repushing ? "vd-spin 0.8s linear infinite" : "none",
                      transformOrigin: "center",
                      transformBox: "fill-box",
                    }}
                  />
                  <span>{repushing ? "Repushing…" : "Repush"}</span>
                </button>
              </div>
            )}

            {editing ? (
              <EditMetadataForm
                detail={detail}
                form={editForm}
                setForm={setEditForm}
                rooftops={rooftops}
                saving={saving}
                error={saveError}
                onSave={handleSaveEdit}
                onCancel={handleCancelEdit}
              />
            ) : isMobile ? (
              <CompactMetadataBlock
                detail={detail}
                canEdit={canEdit && detail.session_id !== null}
                onEdit={handleStartEdit}
              />
            ) : (
              <MetadataBlock
                detail={detail}
                canEdit={canEdit && detail.session_id !== null}
                onEdit={handleStartEdit}
              />
            )}

            <div
              style={{
                ...galleryActionsRowStyle,
                ...(isMobile && {
                  flexDirection: "column",
                  alignItems: "stretch",
                }),
              }}
            >
              <div
                style={{
                  ...galleryActionsLeftStyle,
                  ...(isMobile && { width: "100%" }),
                }}
              >
                {canUpload && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFilesSelected}
                      style={{ display: "none" }}
                    />
                    <button
                      type="button"
                      onClick={handleUploadClick}
                      disabled={uploading}
                      style={{
                        ...repushButtonStyle(uploading),
                        ...(isMobile && {
                          width: "100%",
                          justifyContent: "center",
                          padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
                          fontSize: 14,
                        }),
                      }}
                    >
                      <Upload size={14} strokeWidth={2} />
                      <span>
                        {uploading && uploadProgress
                          ? `Uploading ${uploadProgress.done} of ${uploadProgress.total}…`
                          : "Upload Images"}
                      </span>
                    </button>
                    {uploadError && (
                      <span style={repushErrorStyle}>{uploadError}</span>
                    )}
                  </>
                )}
                {canProcess && (
                  <>
                    <button
                      type="button"
                      onClick={handleProcessClick}
                      disabled={processing || photos.length === 0}
                      title={
                        photos.length === 0
                          ? "No photos to process"
                          : "Run AI background removal on this vehicle's photos"
                      }
                      style={{
                        ...repushButtonStyle(processing || photos.length === 0),
                        ...(isMobile && {
                          width: "100%",
                          justifyContent: "center",
                          padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
                          fontSize: 14,
                        }),
                      }}
                    >
                      <Sparkles
                        size={14}
                        strokeWidth={2}
                        style={{
                          animation: processing ? "vd-spin 0.8s linear infinite" : "none",
                          transformOrigin: "center",
                          transformBox: "fill-box",
                        }}
                      />
                      <span>
                        {processing
                          ? `Processing ${processingDoneCount} of ${photos.length}…`
                          : "Process Images"}
                      </span>
                    </button>
                    {processError && (
                      <span style={repushErrorStyle}>{processError}</span>
                    )}
                    {photos.some((p) => p.pending_processed_url) && !processing && (
                      <button
                        type="button"
                        onClick={handleApproveClick}
                        disabled={approving}
                        title="Commit the processed versions so pushes to the dealer site use them"
                        style={{
                          ...repushButtonStyle(approving),
                          ...(isMobile && {
                            width: "100%",
                            justifyContent: "center",
                            padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
                            fontSize: 14,
                          }),
                        }}
                      >
                        <Check size={14} strokeWidth={2} />
                        <span>{approving ? "Approving…" : "Approve Photos"}</span>
                      </button>
                    )}
                    {approveError && (
                      <span style={repushErrorStyle}>{approveError}</span>
                    )}
                  </>
                )}
              </div>
              <div
                style={{
                  ...galleryActionsRightStyle,
                  ...(isMobile && { width: "100%", justifyContent: "space-between" }),
                }}
              >
                <button
                  type="button"
                  onClick={handleDownloadClick}
                  disabled={downloading || photos.length === 0}
                  title={photos.length === 0 ? "No photos to download" : undefined}
                  style={{
                    ...secondaryButtonStyle,
                    cursor:
                      downloading || photos.length === 0 ? "not-allowed" : "pointer",
                    opacity: downloading || photos.length === 0 ? 0.5 : 1,
                    ...(isMobile && { flex: 1, justifyContent: "center" }),
                  }}
                >
                  <Download size={14} strokeWidth={2} />
                  <span>{downloading ? "Preparing…" : isMobile ? "Download" : "Download Images"}</span>
                </button>
                {downloadError && (
                  <span style={repushErrorStyle}>{downloadError}</span>
                )}
                {canDelete && (
                  <>
                    <button
                      type="button"
                      onClick={handleDeleteAllClick}
                      // Not while processing: deleting mid-run left the
                      // progress poll spinning on a job that no longer
                      // exists for 10 minutes.
                      disabled={deleting || processing || photos.length === 0}
                      title={photos.length === 0 ? "No photos to delete" : undefined}
                      style={{
                        ...dangerButtonStyle,
                        cursor:
                          deleting || processing || photos.length === 0
                            ? "not-allowed"
                            : "pointer",
                        opacity: deleting || processing || photos.length === 0 ? 0.5 : 1,
                        ...(isMobile && { flex: 1, justifyContent: "center" }),
                      }}
                    >
                      <Trash2 size={14} strokeWidth={2} />
                      <span>{deleting ? "Deleting…" : isMobile ? "Delete All" : "Delete All Images"}</span>
                    </button>
                    {deleteError && (
                      <span style={repushErrorStyle}>{deleteError}</span>
                    )}
                  </>
                )}
              </div>
            </div>

            {reorderError && (
              <div style={errorBannerStyle}>Reorder failed: {reorderError}</div>
            )}
            <Gallery
              photos={photos}
              loading={loading}
              isMobile={isMobile}
              canReorder={canUpload}
              recentlyMovedPhotoId={recentlyMovedPhotoId}
              onOpen={(i) => setLightboxIndex(i)}
              onReorder={handleReorder}
            />
          </>
        )}

        {lightboxPhoto && (
          <Lightbox
            photo={lightboxPhoto}
            currentIndex={lightboxIndex ?? 0}
            total={photos.length}
            onClose={() => setLightboxIndex(null)}
            onPrev={() => setLightboxIndex((i) => (i === null ? null : Math.max(i - 1, 0)))}
            onNext={() =>
              setLightboxIndex((i) =>
                i === null ? null : Math.min(i + 1, photos.length - 1),
              )
            }
          />
        )}

        {confirmingReprocess && (
          <ReprocessConfirmModal
            photoCount={photos.length}
            onCancel={() => setConfirmingReprocess(false)}
            onConfirm={() => {
              setConfirmingReprocess(false)
              void startProcessing(true)
            }}
          />
        )}
      </div>
      <style>{ANIMATION_CSS}</style>
    </Layout>
  )
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

const ANIMATION_CSS = `
@keyframes vd-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes vd-photo-saved {
  0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.6); }
  18%  { opacity: 1; transform: translate(-50%, -50%) scale(1.0); }
  75%  { opacity: 1; transform: translate(-50%, -50%) scale(1.0); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
}
`

function MetadataBlock({
  detail,
  canEdit,
  onEdit,
}: {
  detail: VehicleDetail
  canEdit: boolean
  onEdit: () => void
}) {
  return (
    <div style={metadataBlockStyle}>
      {canEdit && (
        <div style={metadataEditRowStyle}>
          <button
            type="button"
            onClick={onEdit}
            style={repushButtonStyle(false)}
          >
            <Pencil size={14} strokeWidth={2} />
            <span>Edit Vehicle Details</span>
          </button>
        </div>
      )}

      <Section label="Dealer">
        <div style={dealerNameStyle}>
          <span>{detail.rooftop_name}</span>
          <InFeedStatusChip status={detail.in_feed_status} />
        </div>
      </Section>

      <SectionDivider />

      <Section label="Vehicle">
        <div style={metadataGridStyle}>
          <Field label="VIN">
            {detail.vin ? (
              <span style={vinValueStyle}>{detail.vin}</span>
            ) : (
              <span style={{ ...vinValueStyle, fontStyle: "italic", color: THEME.colors.neutral.light.textSecondary }}>
                No VIN
              </span>
            )}
          </Field>
          <Field label="Stock"  value={detail.stock} />
          <Field label="Year"   value={detail.year} />
          <Field label="Make"   value={detail.make} />
          <Field label="Model"  value={detail.model} />
        </div>
      </Section>

      <SectionDivider />

      <div style={sectionWrapStyle}>
        <div style={metadataGridStyle}>
          <Field label="Photographer" value={detail.photographer_username} />
          <Field label="Captured"     value={formatDate(detail.captured_at)} />
          <Field label="Pushed"       value={formatDate(detail.pushed_at)} />
          <Field label="Photos"       value={detail.photo_count.toLocaleString()} />
          <Field label="Export Status">
            <ExportStatusChip status={detail.status} />
          </Field>
        </div>
        {detail.last_export_error && (
          <div style={lastErrorStyle}>
            <span style={fieldLabelStyle}>Last export error</span>
            <span style={lastErrorValueStyle}>{detail.last_export_error}</span>
          </div>
        )}
      </div>
    </div>
  )
}

function CompactMetadataBlock({
  detail,
  canEdit,
  onEdit,
}: {
  detail: VehicleDetail
  canEdit: boolean
  onEdit: () => void
}) {
  return (
    <div style={compactMetadataBlockStyle}>
      {canEdit && (
        <div style={metadataEditRowStyle}>
          <button
            type="button"
            onClick={onEdit}
            style={repushButtonStyle(false)}
          >
            <Pencil size={14} strokeWidth={2} />
            <span>Edit Vehicle Details</span>
          </button>
        </div>
      )}

      <CompactField label="Dealer">
        <div style={compactDealerValueStyle}>
          <span>{detail.rooftop_name}</span>
          <InFeedStatusChip status={detail.in_feed_status} />
        </div>
      </CompactField>

      <div style={compactDividerStyle} />

      <CompactField label="VIN">
        {detail.vin ? (
          <span style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' }}>
            {detail.vin}
          </span>
        ) : (
          <span style={{ fontStyle: "italic", color: THEME.colors.neutral.light.textSecondary }}>
            No VIN
          </span>
        )}
      </CompactField>
      <CompactField label="Stock" value={detail.stock} />
      <CompactField label="Year"  value={detail.year} />
      <CompactField label="Make"  value={detail.make} />
      <CompactField label="Model" value={detail.model} />

      <div style={compactDividerStyle} />

      <CompactField label="Photographer" value={detail.photographer_username} />
      <CompactField label="Captured"     value={formatDate(detail.captured_at)} />
      <CompactField label="Pushed"       value={formatDate(detail.pushed_at)} />
      <CompactField label="Photos"       value={detail.photo_count.toLocaleString()} />
      <CompactField label="Status">
        <ExportStatusChip status={detail.status} />
      </CompactField>

      {detail.last_export_error && (
        <>
          <div style={compactDividerStyle} />
          <div style={compactPhotographerRowStyle}>
            <span style={fieldLabelStyle}>Last export error</span>
            <span style={lastErrorValueStyle}>{detail.last_export_error}</span>
          </div>
        </>
      )}
    </div>
  )
}

function CompactField({
  label,
  value,
  children,
}: {
  label: string
  value?: string | number | null
  children?: React.ReactNode
}) {
  const isEmpty =
    children === undefined &&
    (value === null || value === undefined || value === "")
  return (
    <div style={compactFieldRowStyle}>
      <span style={compactFieldLabelStyle}>{label}</span>
      {children !== undefined ? (
        <div style={compactFieldValueStyle}>{children}</div>
      ) : (
        <span style={compactFieldValueStyle}>{isEmpty ? "—" : value}</span>
      )}
    </div>
  )
}

function Section({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div style={sectionWrapStyle}>
      <div style={sectionHeaderStyle}>{label}</div>
      {children}
    </div>
  )
}

function SectionDivider() {
  return <div style={sectionDividerStyle} />
}

function EditMetadataForm({
  detail,
  form,
  setForm,
  rooftops,
  saving,
  error,
  onSave,
  onCancel,
}: {
  detail: VehicleDetail
  form: EditFormState
  setForm: React.Dispatch<React.SetStateAction<EditFormState>>
  rooftops: RooftopOption[]
  saving: boolean
  error: string | null
  onSave: () => void
  onCancel: () => void
}) {
  const update = (key: keyof EditFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }))

  // If the rooftops fetch hasn't completed (or failed) keep the current
  // dealer selectable so we don't lose the user's context.
  const dealerOptions =
    rooftops.length > 0
      ? rooftops
      : [{ slug: detail.rooftop_slug, name: detail.rooftop_name }]

  return (
    <div style={metadataBlockStyle}>
      {error && <div style={editFormErrorStyle}>{error}</div>}

      <Section label="Dealer">
        <select
          value={form.rooftop_slug}
          onChange={update("rooftop_slug")}
          style={editInputStyle}
          disabled={saving}
          aria-label="Dealer"
        >
          {dealerOptions.map((d) => (
            <option key={d.slug} value={d.slug}>
              {d.name}
            </option>
          ))}
        </select>
      </Section>

      <SectionDivider />

      <Section label="Vehicle">
        <div style={metadataGridStyle}>
          <EditField label="VIN" htmlFor="edit-vin">
            <input
              id="edit-vin"
              type="text"
              value={form.vin}
              onChange={update("vin")}
              style={editInputMonoStyle}
              disabled={saving}
              spellCheck={false}
              autoCapitalize="characters"
            />
          </EditField>
          <EditField label="Stock" htmlFor="edit-stock">
            <input
              id="edit-stock"
              type="text"
              value={form.stock}
              onChange={update("stock")}
              style={editInputStyle}
              disabled={saving}
            />
          </EditField>
          <EditField label="Year" htmlFor="edit-year">
            <input
              id="edit-year"
              type="number"
              value={form.year}
              onChange={update("year")}
              style={editInputStyle}
              disabled={saving}
              min={1900}
              max={2100}
            />
          </EditField>
          <EditField label="Make" htmlFor="edit-make">
            <input
              id="edit-make"
              type="text"
              value={form.make}
              onChange={update("make")}
              style={editInputStyle}
              disabled={saving}
            />
          </EditField>
          <EditField label="Model" htmlFor="edit-model">
            <input
              id="edit-model"
              type="text"
              value={form.model}
              onChange={update("model")}
              style={editInputStyle}
              disabled={saving}
            />
          </EditField>
        </div>
      </Section>

      <SectionDivider />

      <Section label="Photo Activity">
        <div style={metadataGridStyle}>
          <Field label="Photographer" value={detail.photographer_username} />
          <Field label="Captured"     value={formatDate(detail.captured_at)} />
          <Field label="Pushed"       value={formatDate(detail.pushed_at)} />
          <Field label="Photos"       value={detail.photo_count.toLocaleString()} />
          <Field label="Export Status">
            <ExportStatusChip status={detail.status} />
          </Field>
        </div>
      </Section>

      <div style={editFormActionsStyle}>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={secondaryButtonStyle}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          style={repushButtonStyle(saving)}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  )
}

function EditField({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <div style={fieldStyle}>
      <label htmlFor={htmlFor} style={fieldLabelStyle}>
        {label}
      </label>
      {children}
    </div>
  )
}

function Field({
  label,
  value,
  children,
}: {
  label: string
  value?: string | number | null
  children?: React.ReactNode
}) {
  return (
    <div style={fieldStyle}>
      <span style={fieldLabelStyle}>{label}</span>
      {children !== undefined ? (
        <div>{children}</div>
      ) : (
        <span style={fieldValueStyle}>
          {value === null || value === undefined || value === "" ? "—" : value}
        </span>
      )}
    </div>
  )
}

function Gallery({
  photos,
  loading,
  isMobile,
  canReorder,
  recentlyMovedPhotoId,
  onOpen,
  onReorder,
}: {
  photos: IndexPhoto[]
  loading: boolean
  isMobile: boolean
  canReorder: boolean
  recentlyMovedPhotoId: number | null
  onOpen: (index: number) => void
  onReorder: (newPhotoIds: number[], movedPhotoId: number) => void
}) {
  // PointerSensor covers both mouse and touch. `delay: 200, tolerance: 5`
  // means a quick tap-and-release opens the lightbox (no drag); a press
  // held ≥200ms enters drag mode. The 5px tolerance allows for finger
  // tremor on touch during the delay window.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  )

  const gridStyle: React.CSSProperties = {
    ...galleryGridStyle,
    ...(isMobile && { gridTemplateColumns: "repeat(2, 1fr)" }),
  }

  if (loading) {
    return <div style={emptyStateStyle}>Loading photos…</div>
  }
  if (photos.length === 0) {
    return <div style={emptyStateStyle}>No photos uploaded yet for this vehicle.</div>
  }

  // Users without the reorder permission see a plain non-sortable gallery;
  // photos still open in the lightbox on click.
  if (!canReorder) {
    return (
      <>
        <div style={sectionLabelStyle}>Photos ({photos.length})</div>
        <div style={gridStyle}>
          {photos.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onOpen(i)}
              style={thumbnailButtonStyle}
            >
              <img
                src={displayPhotoUrl(p)}
                alt={`Vehicle ${p.display_order + 1}`}
                style={thumbnailImageStyle}
                loading="lazy"
              />
            </button>
          ))}
        </div>
      </>
    )
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = photos.findIndex((p) => p.id === active.id)
    const newIndex = photos.findIndex((p) => p.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    const reordered = arrayMove(photos, oldIndex, newIndex)
    onReorder(reordered.map((p) => p.id), Number(active.id))
  }

  return (
    <>
      <div style={sectionLabelStyle}>
        Photos ({photos.length})
        <span style={reorderHintStyle}>Drag to reorder</span>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={photos.map((p) => p.id)} strategy={rectSortingStrategy}>
          <div style={gridStyle}>
            {photos.map((p, i) => (
              <SortablePhotoTile
                key={p.id}
                photo={p}
                index={i}
                justSaved={p.id === recentlyMovedPhotoId}
                onOpen={onOpen}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </>
  )
}

function SortablePhotoTile({
  photo,
  index,
  justSaved,
  onOpen,
}: {
  photo: IndexPhoto
  index: number
  justSaved: boolean
  onOpen: (index: number) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id })

  const tileStyle: React.CSSProperties = {
    ...thumbnailButtonStyle,
    position: "relative",
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    cursor: isDragging ? "grabbing" : "pointer",
    touchAction: "none",
  }

  return (
    <div
      ref={setNodeRef}
      style={tileStyle}
      {...attributes}
      {...listeners}
      onClick={() => {
        if (!isDragging) onOpen(index)
      }}
    >
      <img
        src={displayPhotoUrl(photo)}
        alt={`Vehicle ${photo.display_order + 1}`}
        style={thumbnailImageStyle}
        loading="lazy"
        draggable={false}
      />
      {justSaved && (
        <div style={justSavedBadgeStyle} aria-label="Reorder saved">
          <Check size={22} strokeWidth={3} color={THEME.colors.white} />
        </div>
      )}
    </div>
  )
}

function Lightbox({
  photo,
  currentIndex,
  total,
  onClose,
  onPrev,
  onNext,
}: {
  photo: IndexPhoto
  currentIndex: number
  total: number
  onClose: () => void
  onPrev: () => void
  onNext: () => void
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={lightboxBackdropStyle}
      onClick={onClose}
    >
      <div style={lightboxToolbarStyle}>
        <span style={lightboxCounterStyle}>
          {currentIndex + 1} / {total}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onClose()
          }}
          style={lightboxCloseStyle}
          aria-label="Close"
        >
          <X size={18} strokeWidth={2} />
        </button>
      </div>
      <img
        src={displayPhotoUrl(photo)}
        alt={`Vehicle ${photo.display_order + 1}`}
        style={lightboxImageStyle}
        onClick={(e) => e.stopPropagation()}
      />
      {currentIndex > 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onPrev()
          }}
          style={{ ...lightboxNavStyle, left: 24 }}
          aria-label="Previous"
        >
          ‹
        </button>
      )}
      {currentIndex < total - 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onNext()
          }}
          style={{ ...lightboxNavStyle, right: 24 }}
          aria-label="Next"
        >
          ›
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Read pixel width/height from an image File via an off-DOM <img>.
 * Resolves to null if the file isn't a decodable image — confirm() will
 * just persist nulls in that case rather than blocking the upload.
 */
function readImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }
    img.src = url
  })
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null
  const withTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`
  const d = new Date(withTz)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const containerStyle: React.CSSProperties = {
  padding: THEME.spacing.lg,
  fontFamily: THEME.fonts.primary,
}

const backLinkStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: THEME.spacing.xs,
  color: THEME.colors.neutral.light.textSecondary,
  textDecoration: "none",
  fontSize: 13,
  marginBottom: THEME.spacing.sm,
}

const titleStyle: React.CSSProperties = {
  fontSize: 24,
  fontWeight: 500,
  letterSpacing: "-0.5px",
  color: THEME.colors.neutral.light.textPrimary,
  margin: 0,
  marginBottom: THEME.spacing.lg,
}

const errorBannerStyle: React.CSSProperties = {
  background: tint(THEME.colors.semantic.danger, 0.1),
  border: `1px solid ${THEME.colors.semantic.danger}`,
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  color: THEME.colors.semantic.danger,
  fontSize: 13,
  marginBottom: THEME.spacing.md,
}

const editHeaderRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: THEME.spacing.sm,
  marginBottom: THEME.spacing.sm,
}

const metadataBlockStyle: React.CSSProperties = {
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  padding: THEME.spacing.lg,
  marginBottom: THEME.spacing.md,
}

const metadataEditRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-start",
  marginBottom: THEME.spacing.md,
}

const sectionWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
}

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const sectionDividerStyle: React.CSSProperties = {
  height: 1,
  background: THEME.colors.silver,
  opacity: 0.6,
  margin: `${THEME.spacing.md} 0`,
}

const dealerNameStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
  display: "flex",
  alignItems: "center",
  gap: THEME.spacing.sm,
}

const vinValueStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
}

const metadataGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: THEME.spacing.md,
}

const fieldStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
}

const fieldLabelStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const fieldValueStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textPrimary,
}

const lastErrorStyle: React.CSSProperties = {
  marginTop: THEME.spacing.md,
  display: "flex",
  flexDirection: "column",
  gap: 2,
}

const lastErrorValueStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.semantic.danger,
  fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
}

const galleryActionsRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: THEME.spacing.sm,
  marginBottom: THEME.spacing.md,
}

const galleryActionsLeftStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.sm,
  alignItems: "center",
}

const galleryActionsRightStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.sm,
  alignItems: "center",
}

const dangerButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: THEME.spacing.xs,
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: 500,
  borderRadius: THEME.borderRadius.md,
  border: `1px solid ${THEME.colors.semantic.danger}`,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.semantic.danger,
  fontFamily: THEME.fonts.primary,
  cursor: "pointer",
}

const secondaryButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: THEME.spacing.xs,
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: 500,
  borderRadius: THEME.borderRadius.md,
  border: `1px solid ${THEME.colors.silver}`,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: THEME.fonts.primary,
  cursor: "pointer",
}

const editFormErrorStyle: React.CSSProperties = {
  background: tint(THEME.colors.semantic.danger, 0.1),
  border: `1px solid ${THEME.colors.semantic.danger}`,
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  color: THEME.colors.semantic.danger,
  fontSize: 13,
  marginBottom: THEME.spacing.sm,
}

const editInputStyle: React.CSSProperties = {
  fontSize: 13,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: THEME.fonts.primary,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
}

const editInputMonoStyle: React.CSSProperties = {
  ...editInputStyle,
  fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
}

const editFormActionsStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.sm,
  justifyContent: "flex-end",
}

const repushButtonStyle = (disabled: boolean): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  gap: THEME.spacing.xs,
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: 500,
  borderRadius: THEME.borderRadius.md,
  border: `1px solid ${THEME.colors.navy}`,
  background: disabled ? tint(THEME.colors.navy, 0.4) : THEME.colors.navy,
  color: THEME.colors.white,
  fontFamily: THEME.fonts.primary,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.7 : 1,
})

const repushErrorStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.semantic.danger,
}

const sectionLabelStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
  marginBottom: THEME.spacing.sm,
  display: "flex",
  alignItems: "baseline",
  gap: THEME.spacing.sm,
}

const reorderHintStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 400,
  letterSpacing: 0,
  textTransform: "none",
  color: THEME.colors.neutral.light.textSecondary,
  opacity: 0.7,
}

const justSavedBadgeStyle: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 48,
  height: 48,
  borderRadius: "50%",
  background: "rgba(13, 26, 66, 0.78)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
  animation: "vd-photo-saved 1800ms ease-out forwards",
}

const galleryGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
  gap: THEME.spacing.sm,
}

const thumbnailButtonStyle: React.CSSProperties = {
  padding: 0,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  cursor: "pointer",
  overflow: "hidden",
  aspectRatio: "4 / 3",
}

const thumbnailImageStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
}

const emptyStateStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  padding: THEME.spacing.lg,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  textAlign: "center",
}

const lightboxBackdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.85)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
}

const lightboxToolbarStyle: React.CSSProperties = {
  position: "absolute",
  top: 16,
  right: 16,
  display: "flex",
  alignItems: "center",
  gap: THEME.spacing.md,
  color: THEME.colors.white,
}

const lightboxCounterStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.white,
  fontFamily: THEME.fonts.primary,
}

const lightboxCloseStyle: React.CSSProperties = {
  background: "rgba(255, 255, 255, 0.15)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  borderRadius: THEME.borderRadius.md,
  padding: 6,
  color: THEME.colors.white,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}

const lightboxImageStyle: React.CSSProperties = {
  maxWidth: "90vw",
  maxHeight: "90vh",
  objectFit: "contain",
  borderRadius: THEME.borderRadius.md,
}

const compactMetadataBlockStyle: React.CSSProperties = {
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  padding: THEME.spacing.md,
  marginBottom: THEME.spacing.md,
  display: "flex",
  flexDirection: "column",
  gap: 2,
}

const compactDividerStyle: React.CSSProperties = {
  height: 1,
  background: THEME.colors.silver,
  opacity: 0.6,
  margin: `${THEME.spacing.xs} 0`,
}

const compactFieldRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: THEME.spacing.sm,
  padding: "3px 0",
}

const compactFieldLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
  flex: "0 0 120px",
}

const compactFieldValueStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textPrimary,
  flex: 1,
  minWidth: 0,
  wordBreak: "break-word",
}

const compactDealerValueStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: THEME.spacing.sm,
}

const compactPhotographerRowStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  marginTop: THEME.spacing.xs,
}

const lightboxNavStyle: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: 44,
  height: 44,
  borderRadius: "50%",
  background: "rgba(255, 255, 255, 0.15)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  color: THEME.colors.white,
  fontSize: 28,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  paddingBottom: 4,
}
